import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import MentionList, { MentionItem, MentionListHandle } from './MentionList';

/** 멘션 후보 사용자. */
export interface MentionUser {
  id: number;
  name: string;
}

/** suggestion 팝업을 캐럿 위치(clientRect) 아래에 고정 배치. */
function positionPopup(popup: HTMLElement, clientRect?: (() => DOMRect | null) | null): void {
  if (!clientRect) return;
  const rect = clientRect();
  if (!rect) return;
  popup.style.left = `${rect.left}px`;
  popup.style.top = `${rect.bottom + 4}px`;
}

/**
 * `@` 멘션 확장 — 입력 시 사용자 목록 팝업, 선택하면 멘션 칩 삽입.
 *
 * 저장 마크다운에는 `@[이름](user:123)` 로 직렬화해 user_id 를 보존한다(백엔드가
 * 이 패턴을 파싱해 멘션된 사용자에게 알림 생성). 재로딩 시에는 표준 마크다운 링크로
 * 렌더되지만 본문/알림 동작에는 영향이 없다.
 */
export function createMention(getUsers: () => MentionUser[]) {
  return Mention.extend({
    addStorage() {
      return {
        markdown: {
          serialize(state: { write: (s: string) => void }, node: { attrs: { id: unknown; label?: unknown } }) {
            const id = node.attrs.id;
            const label = (node.attrs.label as string) ?? String(id);
            state.write(`@[${label}](user:${id})`);
          },
          parse: {},
        },
      };
    },
  }).configure({
    HTMLAttributes: { class: 'mention' },
    suggestion: {
      char: '@',
      items: ({ query }: { query: string }): MentionItem[] => {
        const q = query.trim().toLowerCase();
        return getUsers()
          .filter((u) => u.name && (q === '' || u.name.toLowerCase().includes(q)))
          .slice(0, 8)
          .map((u) => ({ id: u.id, label: u.name }));
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      command: ({ editor, range, props }: any) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            { type: 'mention', attrs: props },
            { type: 'text', text: ' ' },
          ])
          .run();
      },
      render: () => {
        let renderer: ReactRenderer<MentionListHandle> | null = null;
        let popup: HTMLElement | null = null;
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onStart: (props: any) => {
            renderer = new ReactRenderer(MentionList, { props, editor: props.editor });
            popup = document.createElement('div');
            popup.style.position = 'fixed';
            popup.style.zIndex = '2100';
            popup.appendChild(renderer.element);
            document.body.appendChild(popup);
            positionPopup(popup, props.clientRect);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onUpdate: (props: any) => {
            renderer?.updateProps(props);
            if (popup) positionPopup(popup, props.clientRect);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onKeyDown: (props: any) => {
            if (props.event.key === 'Escape') {
              popup?.remove();
              return true;
            }
            return renderer?.ref?.onKeyDown(props) ?? false;
          },
          onExit: () => {
            popup?.remove();
            popup = null;
            renderer?.destroy();
            renderer = null;
          },
        };
      },
    },
  });
}
